import { useCallback, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { getUploadErrorText, isUploadAbortedError } from '@shared/helpers';
import { OptionType } from '@shared/ui/MultiSelect';

import { uploadProgress } from '../../../store/slices/dashboardSlice/action';
import {
    ProgressContext,
    UploadedProgressMediaItem,
    UploadMediaItem,
} from '../Context/ProgressContext';

type UploadMediaMeta = {
    date: string;
    description: string;
    albums: OptionType[];
};

type GetUploadMediaMeta = (mediaItem: UploadMediaItem) => UploadMediaMeta;

export const useUploadProgressMediaItem = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        getUploadMediaListSnapshot,
        setUploadMediaError,
        setUploadMediaUploadingState,
        setUploadAbortController,
        removeUploadMediaItems,
        addUploadedProgressMediaItems,
    } = useContext(ProgressContext);

    const uploadMediaItem = useCallback(
        async (id: string, getMeta: GetUploadMediaMeta) => {
            if (!id) {
                return;
            }

            const mediaItem = getUploadMediaListSnapshot().find((item) => item.id === id);
            if (!mediaItem) {
                return;
            }

            setUploadMediaError([id], '');
            setUploadMediaUploadingState([id], true);
            const abortController = new AbortController();
            setUploadAbortController(id, abortController);

            try {
                const { date, description, albums } = getMeta(mediaItem);
                const formData = new FormData();
                formData.append('files[0]', mediaItem.file);
                formData.append('dates[0]', date);
                formData.append('description[0]', description);
                albums.forEach((album) => {
                    formData.append('albums[0][]', album.title);
                });

                const uploadedMediaItems = (await dispatch(
                    uploadProgress({ formData, signal: abortController.signal })
                ).unwrap()) as UploadedProgressMediaItem[];

                addUploadedProgressMediaItems(uploadedMediaItems);
                removeUploadMediaItems([id]);
            } catch (error) {
                const isAborted = isUploadAbortedError(error);
                const errorText = getUploadErrorText(error, {
                    defaultErrorText: t('useConstructionProgressBlock.upload.defaultError'),
                    offlineErrorText:
                        t('Errors.UploadErrorText.text') || 'Нет соединения с Интернетом',
                });

                if (isAborted) {
                    removeUploadMediaItems([id]);
                } else {
                    setUploadMediaError(
                        [id],
                        errorText || t('useConstructionProgressBlock.upload.defaultError')
                    );
                }

                throw error;
            } finally {
                setUploadMediaUploadingState([id], false);
                setUploadAbortController(id, null);
            }
        },
        [
            addUploadedProgressMediaItems,
            dispatch,
            getUploadMediaListSnapshot,
            removeUploadMediaItems,
            setUploadAbortController,
            setUploadMediaError,
            setUploadMediaUploadingState,
            t,
        ]
    );

    return { uploadMediaItem };
};
